/**
 * Standalone 3D model viewer for embedding in Flutter web via HtmlElementView.
 *
 * Exposes a simple imperative API on `window.ModelViewer` that creates/destroys
 * viewer instances in arbitrary DOM containers.
 *
 * Reuses the streamline project's model loading pipeline (Web Workers + WASM)
 * and Three.js scene setup for consistent rendering quality.
 */

import * as THREE from 'three';
import { RoomEnvironment } from 'three-stdlib';
import {
  EffectComposer,
  FXAAShader,
  RenderPass,
  ShaderPass,
  SSAOPass,
} from 'three-stdlib';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';

// ============================================================================
// Types
// ============================================================================

type ViewMode = 'solid' | 'wireframe' | 'xray';

interface ViewerInstance {
  /** Current view mode */
  viewMode: ViewMode;
  /** Switch view mode */
  setViewMode(mode: ViewMode): void;
  /** Reset camera to default position */
  resetCamera(): void;
  /** Dispose all resources */
  destroy(): void;
}

interface ModelViewerAPI {
  create(container: HTMLElement, url: string, fileName: string): ViewerInstance;
}

// ============================================================================
// Model loading (inlined from streamline modelLoader.ts)
// ============================================================================

const MODEL_COLOR = 0x8a9099;

function createModelMaterial(
  options: { color?: number; flatShading?: boolean; vertexColors?: boolean } = {},
): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: options.color ?? MODEL_COLOR,
    roughness: 0.55,
    metalness: 0.1,
    envMapIntensity: 0.8,
    side: THREE.DoubleSide,
    flatShading: options.flatShading ?? false,
    vertexColors: options.vertexColors ?? false,
  });
}

enum FileType {
  STL = 'stl',
  THREE_MF = '3mf',
  OBJ = 'obj',
  STEP = 'step',
  UNKNOWN = 'unknown',
}

function detectFileType(fileName: string): FileType {
  const extension = fileName.toLowerCase().split('.').pop();
  switch (extension) {
    case 'stl': return FileType.STL;
    case '3mf': return FileType.THREE_MF;
    case 'obj': return FileType.OBJ;
    case 'step':
    case 'stp': return FileType.STEP;
    default: return FileType.UNKNOWN;
  }
}

// ---------------------------------------------------------------------------
// STL Worker (inline)
// ---------------------------------------------------------------------------

function createSTLWorkerBlob(): Blob {
  const code = `
    function ensureBinary(data) {
      if (typeof data === 'string') {
        const buf = new Uint8Array(data.length);
        for (let i = 0; i < data.length; i++) buf[i] = data.charCodeAt(i) & 0xff;
        return buf.buffer;
      }
      return data;
    }
    function ensureString(data) {
      if (typeof data !== 'string') return new TextDecoder().decode(data);
      return data;
    }
    function isBinary(data) {
      const reader = new DataView(data);
      const faceSize = 32/8*3 + 32/8*3*3 + 16/8;
      const nFaces = reader.getUint32(80, true);
      const expect = 80 + 32/8 + nFaces * faceSize;
      if (expect === reader.byteLength) return true;
      const solid = [115,111,108,105,100];
      for (let off = 0; off < 5; off++) {
        let match = true;
        for (let i = 0; i < solid.length; i++) {
          if (solid[i] !== reader.getUint8(off + i)) { match = false; break; }
        }
        if (match) return false;
      }
      return true;
    }
    function srgbToLinear(c) {
      return c < 0.04045 ? c * 0.0773993808 : (c * 0.9478672986 + 0.0521327014) ** 2.4;
    }
    function parseBinary(data) {
      const reader = new DataView(data);
      const faces = reader.getUint32(80, true);
      let hasColors = false, defaultR = 0, defaultG = 0, defaultB = 0, alpha = 1;
      for (let index = 0; index < 80 - 10; index++) {
        if (reader.getUint32(index, false) === 0x434f4c4f && reader.getUint8(index + 4) === 0x52 && reader.getUint8(index + 5) === 0x3d) {
          hasColors = true;
          defaultR = reader.getUint8(index + 6) / 255;
          defaultG = reader.getUint8(index + 7) / 255;
          defaultB = reader.getUint8(index + 8) / 255;
          alpha = reader.getUint8(index + 9) / 255;
        }
      }
      const positions = new Float32Array(faces * 9);
      const normals = new Float32Array(faces * 9);
      let colors = null;
      if (hasColors) colors = new Float32Array(faces * 9);
      const dataOffset = 84, faceLength = 12 * 4 + 2;
      for (let face = 0; face < faces; face++) {
        const start = dataOffset + face * faceLength;
        const nx = reader.getFloat32(start, true), ny = reader.getFloat32(start + 4, true), nz = reader.getFloat32(start + 8, true);
        let r = defaultR, g = defaultG, b = defaultB;
        if (hasColors) {
          const pc = reader.getUint16(start + 48, true);
          if ((pc & 0x8000) === 0) { r = (pc & 0x1f) / 31; g = ((pc >> 5) & 0x1f) / 31; b = ((pc >> 10) & 0x1f) / 31; }
        }
        for (let i = 1; i <= 3; i++) {
          const vs = start + i * 12, idx = face * 9 + (i - 1) * 3;
          positions[idx] = reader.getFloat32(vs, true); positions[idx + 1] = reader.getFloat32(vs + 4, true); positions[idx + 2] = reader.getFloat32(vs + 8, true);
          normals[idx] = nx; normals[idx + 1] = ny; normals[idx + 2] = nz;
          if (colors) { colors[idx] = srgbToLinear(r); colors[idx + 1] = srgbToLinear(g); colors[idx + 2] = srgbToLinear(b); }
        }
      }
      return { positions, normals, colors, alpha, hasColors, groups: [], groupNames: [] };
    }
    function parseASCII(text) {
      const patternSolid = /solid([\\s\\S]*?)endsolid/g;
      const patternFace = /facet([\\s\\S]*?)endfacet/g;
      const patternName = /solid\\s(.+)/;
      const pf = '[\\\\s]+([+-]?(?:\\\\d*)(?:\\\\.\\\\d*)?(?:[eE][+-]?\\\\d+)?)';
      const patternVertex = new RegExp('vertex' + pf + pf + pf, 'g');
      const patternNormal = new RegExp('normal' + pf + pf + pf, 'g');
      const vertices = [], norms = [], groupNames = [], groups = [];
      let groupCount = 0, startVertex = 0, endVertex = 0;
      for (let sm = patternSolid.exec(text); sm !== null; sm = patternSolid.exec(text)) {
        startVertex = endVertex;
        const solid = sm[0], nameMatch = patternName.exec(solid);
        groupNames.push(nameMatch ? nameMatch[1] : '');
        for (let fm = patternFace.exec(solid); fm !== null; fm = patternFace.exec(solid)) {
          const ft = fm[0], nm = patternNormal.exec(ft); patternNormal.lastIndex = 0;
          const nx = nm ? parseFloat(nm[1]) : 0, ny = nm ? parseFloat(nm[2]) : 0, nz = nm ? parseFloat(nm[3]) : 0;
          for (let vm = patternVertex.exec(ft); vm !== null; vm = patternVertex.exec(ft)) {
            vertices.push(parseFloat(vm[1]), parseFloat(vm[2]), parseFloat(vm[3]));
            norms.push(nx, ny, nz); endVertex++;
          }
          patternVertex.lastIndex = 0;
        }
        patternFace.lastIndex = 0;
        groups.push({ start: startVertex, count: endVertex - startVertex, materialIndex: groupCount }); groupCount++;
      }
      return { positions: new Float32Array(vertices), normals: new Float32Array(norms), colors: null, alpha: 1, hasColors: false, groups, groupNames };
    }
    self.onmessage = function(event) {
      try {
        const data = event.data;
        const binData = ensureBinary(data);
        const geometry = isBinary(binData) ? parseBinary(binData) : parseASCII(ensureString(data));
        const transferables = [geometry.positions.buffer, geometry.normals.buffer];
        if (geometry.colors) transferables.push(geometry.colors.buffer);
        self.postMessage({ geometry }, transferables);
      } catch (err) {
        self.postMessage({ error: err.message, stack: err.stack });
      }
    };
  `;
  return new Blob([code], { type: 'application/javascript' });
}

// ---------------------------------------------------------------------------
// OBJ Worker (inline)
// ---------------------------------------------------------------------------

function createOBJWorkerBlob(): Blob {
  // OBJ worker is large — load from the streamline workers via URL import
  // For the standalone bundle, we inline a simplified OBJ parser
  const code = `
    function srgbToLinear(c) { return c < 0.04045 ? c * 0.0773993808 : (c * 0.9478672986 + 0.0521327014) ** 2.4; }
    function cross(ax,ay,az,bx,by,bz) { return [ay*bz-az*by, az*bx-ax*bz, ax*by-ay*bx]; }
    function normalize(x,y,z) { const len = Math.sqrt(x*x+y*y+z*z); return len === 0 ? [0,0,0] : [x/len, y/len, z/len]; }

    function createObjectState(name, fromDeclaration) {
      const obj = {
        name: name || '', fromDeclaration,
        geometry: { vertices: [], normals: [], colors: [], uvs: [], hasUVIndices: false, type: 'Mesh' },
        materials: [], smooth: true,
        startMaterial(matName) {
          const prev = obj._finalize(false);
          if (prev && (prev.inherited || prev.groupCount <= 0)) obj.materials.splice(prev.index, 1);
          obj.materials.push({ index: obj.materials.length, name: matName || '', smooth: prev ? prev.smooth : obj.smooth, groupStart: prev ? prev.groupEnd : 0, groupEnd: -1, groupCount: -1, inherited: false });
        },
        currentMaterial() { return obj.materials.length > 0 ? obj.materials[obj.materials.length - 1] : undefined; },
        _finalize(end) {
          const last = obj.currentMaterial();
          if (last && last.groupEnd === -1) { last.groupEnd = obj.geometry.vertices.length / 3; last.groupCount = last.groupEnd - last.groupStart; last.inherited = false; }
          if (end && obj.materials.length > 1) for (let i = obj.materials.length - 1; i >= 0; i--) { if (obj.materials[i].groupCount <= 0) obj.materials.splice(i, 1); }
          if (end && obj.materials.length === 0) obj.materials.push({ index: 0, name: '', smooth: obj.smooth, groupStart: 0, groupEnd: -1, groupCount: -1, inherited: false });
          return last;
        }
      };
      return obj;
    }

    function createParserState() {
      const state = {
        objects: [], object: null, vertices: [], normals: [], colors: [], uvs: [], materialLibraries: [],
        startObject(name, fromDeclaration) {
          if (state.object && !state.object.fromDeclaration) { state.object.name = name; state.object.fromDeclaration = fromDeclaration !== false; return; }
          const prev = state.object ? state.object.currentMaterial() : null;
          if (state.object) state.object._finalize(true);
          state.object = createObjectState(name, fromDeclaration !== false);
          if (prev && prev.name) state.object.materials.push({ index: 0, name: prev.name, smooth: prev.smooth, groupStart: 0, groupEnd: -1, groupCount: -1, inherited: true });
          state.objects.push(state.object);
        },
        finalize() { if (state.object) state.object._finalize(true); },
        parseVertexIndex(v, len) { const i = parseInt(v, 10); return (i >= 0 ? i - 1 : i + len / 3) * 3; },
        parseNormalIndex(v, len) { const i = parseInt(v, 10); return (i >= 0 ? i - 1 : i + len / 3) * 3; },
        parseUVIndex(v, len) { const i = parseInt(v, 10); return (i >= 0 ? i - 1 : i + len / 2) * 2; },
        addVertex(a, b, c) { const s = state.vertices, d = state.object.geometry.vertices; d.push(s[a],s[a+1],s[a+2],s[b],s[b+1],s[b+2],s[c],s[c+1],s[c+2]); },
        addVertexPoint(a) { const s = state.vertices, d = state.object.geometry.vertices; d.push(s[a],s[a+1],s[a+2]); },
        addVertexLine(a) { const s = state.vertices, d = state.object.geometry.vertices; d.push(s[a],s[a+1],s[a+2]); },
        addNormal(a, b, c) { const s = state.normals, d = state.object.geometry.normals; d.push(s[a],s[a+1],s[a+2],s[b],s[b+1],s[b+2],s[c],s[c+1],s[c+2]); },
        addFaceNormal(a, b, c) {
          const s = state.vertices;
          const [cx2,cy2,cz2] = cross(s[c]-s[b],s[c+1]-s[b+1],s[c+2]-s[b+2], s[a]-s[b],s[a+1]-s[b+1],s[a+2]-s[b+2]);
          const [nx,ny,nz] = normalize(cx2,cy2,cz2);
          const d = state.object.geometry.normals; d.push(nx,ny,nz,nx,ny,nz,nx,ny,nz);
        },
        addColor(a, b, c) {
          const s = state.colors, d = state.object.geometry.colors;
          if (s[a] !== undefined) d.push(s[a],s[a+1],s[a+2]);
          if (b !== undefined && s[b] !== undefined) d.push(s[b],s[b+1],s[b+2]);
          if (c !== undefined && s[c] !== undefined) d.push(s[c],s[c+1],s[c+2]);
        },
        addUV(a, b, c) { const s = state.uvs, d = state.object.geometry.uvs; d.push(s[a],s[a+1],s[b],s[b+1],s[c],s[c+1]); },
        addDefaultUV() { state.object.geometry.uvs.push(0,0,0,0,0,0); },
        addUVLine(a) { const s = state.uvs, d = state.object.geometry.uvs; d.push(s[a],s[a+1]); },
        addFace(a,b,c,ua,ub,uc,na,nb,nc) {
          const vLen = state.vertices.length;
          let ia = state.parseVertexIndex(a, vLen), ib = state.parseVertexIndex(b, vLen), ic = state.parseVertexIndex(c, vLen);
          state.addVertex(ia, ib, ic); state.addColor(ia, ib, ic);
          if (na !== undefined && na !== '') { const nLen = state.normals.length; ia = state.parseNormalIndex(na, nLen); ib = state.parseNormalIndex(nb, nLen); ic = state.parseNormalIndex(nc, nLen); state.addNormal(ia, ib, ic); }
          else { state.addFaceNormal(ia, ib, ic); }
          if (ua !== undefined && ua !== '') { const uvLen = state.uvs.length; ia = state.parseUVIndex(ua, uvLen); ib = state.parseUVIndex(ub, uvLen); ic = state.parseUVIndex(uc, uvLen); state.addUV(ia, ib, ic); state.object.geometry.hasUVIndices = true; }
          else { state.addDefaultUV(); }
        },
        addPointGeometry(vertices) { state.object.geometry.type = 'Points'; const vLen = state.vertices.length; for (const v of vertices) { const i = state.parseVertexIndex(v, vLen); state.addVertexPoint(i); state.addColor(i); } },
        addLineGeometry(vertices, uvs) { state.object.geometry.type = 'Line'; const vLen = state.vertices.length; const uvLen = state.uvs.length; for (const v of vertices) state.addVertexLine(state.parseVertexIndex(v, vLen)); for (const u of uvs) state.addUVLine(state.parseUVIndex(u, uvLen)); }
      };
      state.startObject('', false);
      return state;
    }

    const _object_pattern = /^[og]\\s*(.+)?/;
    const _material_library_pattern = /^mtllib /;
    const _material_use_pattern = /^usemtl /;
    const _map_use_pattern = /^usemap /;
    const _face_sep = /\\s+/;

    function parseOBJ(text) {
      const state = createParserState();
      text = text.replace(/\\r\\n/g, '\\n').replace(/\\\\\\n/g, '');
      const lines = text.split('\\n');
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trimStart();
        if (line.length === 0 || line.charAt(0) === '#') continue;
        const c = line.charAt(0);
        if (c === 'v') {
          const data = line.split(_face_sep);
          switch (data[0]) {
            case 'v': state.vertices.push(parseFloat(data[1]), parseFloat(data[2]), parseFloat(data[3]));
              if (data.length >= 7) state.colors.push(srgbToLinear(parseFloat(data[4])), srgbToLinear(parseFloat(data[5])), srgbToLinear(parseFloat(data[6])));
              else state.colors.push(undefined, undefined, undefined);
              break;
            case 'vn': state.normals.push(parseFloat(data[1]), parseFloat(data[2]), parseFloat(data[3])); break;
            case 'vt': state.uvs.push(parseFloat(data[1]), parseFloat(data[2])); break;
          }
        } else if (c === 'f') {
          const vd = line.slice(1).trim().split(_face_sep);
          const fv = [];
          for (const v of vd) if (v.length > 0) fv.push(v.split('/'));
          const v1 = fv[0];
          for (let j = 1; j < fv.length - 1; j++) { const v2 = fv[j], v3 = fv[j + 1]; state.addFace(v1[0],v2[0],v3[0],v1[1],v2[1],v3[1],v1[2],v2[2],v3[2]); }
        } else if (c === 'l') {
          const parts = line.substring(1).trim().split(' ');
          let lv = [], lu = [];
          if (line.indexOf('/') === -1) lv = parts;
          else for (const p of parts) { const s = p.split('/'); if (s[0] !== '') lv.push(s[0]); if (s[1] !== '') lu.push(s[1]); }
          state.addLineGeometry(lv, lu);
        } else if (c === 'p') { state.addPointGeometry(line.slice(1).trim().split(' ')); }
        else if (_object_pattern.test(line)) { const r = _object_pattern.exec(line); state.startObject(r ? (' ' + r[0].slice(1).trim()).slice(1) : ''); }
        else if (_material_use_pattern.test(line)) state.object.startMaterial(line.substring(7).trim());
        else if (_material_library_pattern.test(line)) state.materialLibraries.push(line.substring(7).trim());
        else if (c === 's') { const parts = line.split(' '); state.object.smooth = parts.length <= 1 || (parts[1].trim().toLowerCase() !== '0' && parts[1].trim().toLowerCase() !== 'off'); const mat = state.object.currentMaterial(); if (mat) mat.smooth = state.object.smooth; }
      }
      state.finalize();
      return state;
    }

    self.onmessage = function(event) {
      try {
        const text = new TextDecoder().decode(event.data);
        const state = parseOBJ(text);
        const objects = [], transferables = [];
        const hasPrimitives = !(state.objects.length === 1 && state.objects[0].geometry.vertices.length === 0);
        if (hasPrimitives) {
          for (const obj of state.objects) {
            const geo = obj.geometry;
            if (geo.vertices.length === 0) continue;
            const positions = new Float32Array(geo.vertices); transferables.push(positions.buffer);
            let normals = null; if (geo.normals.length > 0) { normals = new Float32Array(geo.normals); transferables.push(normals.buffer); }
            let colors = null; if (geo.colors.length > 0 && geo.colors[0] !== undefined) { colors = new Float32Array(geo.colors.map(c => c !== undefined ? c : 0)); transferables.push(colors.buffer); }
            let uvs = null; if (geo.hasUVIndices) { uvs = new Float32Array(geo.uvs); transferables.push(uvs.buffer); }
            objects.push({ name: obj.name, positions, normals, colors, uvs, hasUVIndices: geo.hasUVIndices, type: geo.type, materials: obj.materials.map(m => ({ name: m.name, smooth: m.smooth, groupStart: m.groupStart, groupCount: m.groupCount })) });
          }
        } else if (state.vertices.length > 0) {
          const positions = new Float32Array(state.vertices); transferables.push(positions.buffer);
          let colors = null; if (state.colors.length > 0 && state.colors[0] !== undefined) { colors = new Float32Array(state.colors.map(c => c !== undefined ? c : 0)); transferables.push(colors.buffer); }
          objects.push({ name: 'points', positions, normals: null, colors, uvs: null, hasUVIndices: false, type: 'Points', materials: [{ name: '', smooth: true, groupStart: 0, groupCount: -1 }] });
        }
        self.postMessage({ objects }, transferables);
      } catch (err) { self.postMessage({ error: err.message, stack: err.stack }); }
    };
  `;
  return new Blob([code], { type: 'application/javascript' });
}

// ============================================================================
// Model loader functions
// ============================================================================

async function loadModelFromURL(url: string, fileName: string): Promise<THREE.Group> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`Failed to fetch model: ${response.status}`);
  const data = await response.arrayBuffer();
  return loadModelFromBuffer(data, fileName);
}

async function loadModelFromBuffer(data: ArrayBuffer, fileName: string): Promise<THREE.Group> {
  const fileType = detectFileType(fileName);
  switch (fileType) {
    case FileType.STL: return loadSTL(data);
    case FileType.THREE_MF: return load3MF(data);
    case FileType.OBJ: return loadOBJ(data);
    case FileType.STEP: return loadSTEP(data);
    default: throw new Error(`Unsupported file type: ${fileName}`);
  }
}

async function loadSTL(data: ArrayBuffer): Promise<THREE.Group> {
  const blob = createSTLWorkerBlob();
  const workerUrl = URL.createObjectURL(blob);
  try {
    const geometry = await runWorker<{ geometry: STLGeometry }>(workerUrl, data);
    return rebuildGroupFromSTLGeometry(geometry.geometry);
  } finally {
    URL.revokeObjectURL(workerUrl);
  }
}

async function loadOBJ(data: ArrayBuffer): Promise<THREE.Group> {
  const blob = createOBJWorkerBlob();
  const workerUrl = URL.createObjectURL(blob);
  try {
    const result = await runWorker<{ objects: OBJObject[] }>(workerUrl, data);
    const group = rebuildGroupFromOBJData(result.objects);
    return convertOBJGroup(group);
  } finally {
    URL.revokeObjectURL(workerUrl);
  }
}

async function load3MF(data: ArrayBuffer): Promise<THREE.Group> {
  // 3MF requires WASM — use dynamic import-based worker
  const workerCode = `
    import lib3mfInit from '@3mfconsortium/lib3mf';
    import lib3mfWasmUrl from '@3mfconsortium/lib3mf/wasm?url';
  `;
  // For 3MF, we load inline since the WASM dependency makes blob workers complex.
  // We parse on the main thread for now and can optimize later.
  const { default: lib3mfInit } = await import('@3mfconsortium/lib3mf');
  const lib3mfWasmModule = await import('@3mfconsortium/lib3mf/wasm?url');
  const lib3mfWasmUrl = lib3mfWasmModule.default;

  const lib = await lib3mfInit({
    locateFile: (path: string) => path.endsWith('.wasm') ? lib3mfWasmUrl : path,
  });

  const wrapper = new lib.CWrapper();
  const model = wrapper.CreateModel();
  const reader = model.QueryReader('3mf');
  const virtualPath = '/input.3mf';
  lib.FS.writeFile(virtualPath, new Uint8Array(data));
  reader.ReadFromFile(virtualPath);
  lib.FS.unlink(virtualPath);

  // Build material color map
  const materialColors = new Map<string, number>();
  const baseMaterialGroups = model.GetBaseMaterialGroups();
  while (baseMaterialGroups.MoveNext()) {
    const bmg = baseMaterialGroups.GetCurrentBaseMaterialGroup();
    const resourceId = bmg.GetUniqueResourceID();
    const propIds = bmg.GetAllPropertyIDs();
    for (let j = 0; j < propIds.size(); j++) {
      const pid = propIds.get(j);
      const color = bmg.GetDisplayColor(pid);
      materialColors.set(`${resourceId}:${pid}`, (color.get_Red() << 16) | (color.get_Green() << 8) | color.get_Blue());
    }
  }

  const meshes: Array<{ positions: Float32Array; indices: Uint32Array; color: number | null; name: string }> = [];
  const objectIterator = model.GetObjects();
  while (objectIterator.MoveNext()) {
    const object = objectIterator.GetCurrentObject();
    if (!object.IsMeshObject()) continue;
    const meshObject = model.GetMeshObjectByID(object.GetResourceID());
    const vertexCount = meshObject.GetVertexCount();
    const triangleCount = meshObject.GetTriangleCount();
    if (vertexCount === 0 || triangleCount === 0) continue;

    const positions = new Float32Array(vertexCount * 3);
    for (let i = 0; i < vertexCount; i++) {
      const v = meshObject.GetVertex(i);
      positions[i * 3] = v.get_Coordinates0();
      positions[i * 3 + 1] = v.get_Coordinates1();
      positions[i * 3 + 2] = v.get_Coordinates2();
    }
    const indices = new Uint32Array(triangleCount * 3);
    for (let i = 0; i < triangleCount; i++) {
      const t = meshObject.GetTriangle(i);
      indices[i * 3] = t.get_Indices0();
      indices[i * 3 + 1] = t.get_Indices1();
      indices[i * 3 + 2] = t.get_Indices2();
    }

    let color: number | null = null;
    try {
      const objProp = meshObject.GetObjectLevelProperty();
      if (objProp.return) color = materialColors.get(`${objProp.UniqueResourceID}:${objProp.PropertyID}`) ?? null;
    } catch { /* no property */ }
    if (color === null && triangleCount > 0) {
      try {
        const triProp = meshObject.GetTriangleProperties(0);
        const prop = triProp.Property;
        const rid = prop.get_ResourceID();
        const pid = prop.get_PropertyIDs0();
        if (rid > 0) color = materialColors.get(`${rid}:${pid}`) ?? null;
      } catch { /* no triangle properties */ }
    }
    meshes.push({ positions, indices, color, name: object.GetName() || `mesh_${meshes.length}` });
  }

  const group = rebuildGroupFromMeshes(meshes);
  return convert3MFGroup(group);
}

async function loadSTEP(data: ArrayBuffer): Promise<THREE.Group> {
  // STEP requires occt-import-js WASM — run on main thread
  const { default: occtImportJsInit } = await import('occt-import-js');
  const occtWasmModule = await import('occt-import-js/dist/occt-import-js.wasm?url');
  const occtWasmUrl = occtWasmModule.default;

  const occt = await occtImportJsInit({
    locateFile: (path: string) => path.endsWith('.wasm') ? occtWasmUrl : path,
  });

  const fileBuffer = new Uint8Array(data);
  const result = occt.ReadStepFile(fileBuffer, {
    linearUnit: 'millimeter',
    linearDeflection: 0.1,
    angularDeflection: 0.1,
  });

  if (!result.success) throw new Error('Failed to read STEP file');

  interface StepMesh {
    positions: Float32Array;
    normals: Float32Array | null;
    indices: Uint32Array | null;
    color: number;
    name: string;
  }

  const meshes: StepMesh[] = [];
  if (result.meshes && Array.isArray(result.meshes)) {
    for (let i = 0; i < result.meshes.length; i++) {
      const meshData = result.meshes[i];
      const rawPositions = meshData.attributes?.position;
      if (!rawPositions) continue;
      const posArray = rawPositions.array ?? rawPositions;
      const positions = posArray instanceof Float32Array ? posArray : new Float32Array(posArray);
      let normals: Float32Array | null = null;
      const rawNormals = meshData.attributes?.normal;
      if (rawNormals) {
        const normArray = rawNormals.array ?? rawNormals;
        normals = normArray instanceof Float32Array ? normArray : new Float32Array(normArray);
      }
      let indices: Uint32Array | null = null;
      const rawIndex = meshData.index;
      if (rawIndex) {
        const idxArray = rawIndex.array ?? rawIndex;
        indices = idxArray instanceof Uint32Array ? idxArray : new Uint32Array(idxArray);
      }
      meshes.push({ positions, normals, indices, color: meshData.color ?? 0x808080, name: meshData.name ?? `mesh_${i}` });
    }
  }

  return rebuildGroupFromStepMeshes(meshes);
}

// ============================================================================
// Worker helper
// ============================================================================

function runWorker<T>(workerUrl: string, data: ArrayBuffer): Promise<T> {
  return new Promise((resolve, reject) => {
    const worker = new Worker(workerUrl);
    worker.onmessage = (event: MessageEvent) => {
      worker.terminate();
      if (event.data.error) {
        reject(new Error(event.data.error));
        return;
      }
      resolve(event.data as T);
    };
    worker.onerror = (err) => {
      worker.terminate();
      reject(new Error(`Worker error: ${err.message}`));
    };
    worker.postMessage(data, [data]);
  });
}

// ============================================================================
// Geometry reconstruction (from streamline modelLoader.ts)
// ============================================================================

interface STLGeometry {
  positions: Float32Array;
  normals: Float32Array;
  colors: Float32Array | null;
  alpha: number;
  hasColors: boolean;
  groups: Array<{ start: number; count: number; materialIndex: number }>;
  groupNames: string[];
}

interface OBJObject {
  name: string;
  positions: Float32Array;
  normals: Float32Array | null;
  colors: Float32Array | null;
  uvs: Float32Array | null;
  hasUVIndices: boolean;
  type: string;
  materials: Array<{ name: string; smooth: boolean; groupStart: number; groupCount: number }>;
}

function rebuildGroupFromSTLGeometry(data: STLGeometry): THREE.Group {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(data.positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(data.normals, 3));
  if (data.hasColors && data.colors) {
    geometry.setAttribute('color', new THREE.BufferAttribute(data.colors, 3));
    (geometry as any).hasColors = true;
    (geometry as any).alpha = data.alpha;
  }
  for (const g of data.groups) geometry.addGroup(g.start, g.count, g.materialIndex);
  if (data.groupNames.length > 0) geometry.userData.groupNames = data.groupNames;
  return createModelGroup(geometry);
}

function rebuildGroupFromMeshes(meshes: Array<{ positions: Float32Array; indices: Uint32Array; color: number | null; name: string }>): THREE.Group {
  const group = new THREE.Group();
  for (const meshData of meshes) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(meshData.positions, 3));
    geometry.setIndex(new THREE.BufferAttribute(meshData.indices, 1));
    const material = createModelMaterial({ flatShading: true });
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = meshData.name;
    group.add(mesh);
  }
  return group;
}

function rebuildGroupFromOBJData(objects: OBJObject[]): THREE.Group {
  const container = new THREE.Group();
  for (const obj of objects) {
    const isLine = obj.type === 'Line';
    const isPoints = obj.type === 'Points';
    const hasVertexColors = obj.colors !== null;
    const bufferGeometry = new THREE.BufferGeometry();
    bufferGeometry.setAttribute('position', new THREE.Float32BufferAttribute(obj.positions, 3));
    if (obj.normals) bufferGeometry.setAttribute('normal', new THREE.Float32BufferAttribute(obj.normals, 3));
    if (obj.colors) bufferGeometry.setAttribute('color', new THREE.Float32BufferAttribute(obj.colors, 3));
    if (obj.uvs) bufferGeometry.setAttribute('uv', new THREE.Float32BufferAttribute(obj.uvs, 2));
    const createdMaterials: THREE.Material[] = [];
    for (const matInfo of obj.materials) {
      let material: THREE.Material;
      if (isLine) material = new THREE.LineBasicMaterial();
      else if (isPoints) material = new THREE.PointsMaterial({ size: 1, sizeAttenuation: false });
      else material = createModelMaterial({ flatShading: !matInfo.smooth, vertexColors: hasVertexColors });
      material.name = matInfo.name;
      createdMaterials.push(material);
    }
    if (createdMaterials.length > 1) {
      for (let mi = 0; mi < obj.materials.length; mi++) {
        bufferGeometry.addGroup(obj.materials[mi].groupStart, obj.materials[mi].groupCount, mi);
      }
    }
    let mesh: THREE.Object3D;
    const matOrMats = createdMaterials.length > 1 ? createdMaterials : createdMaterials[0];
    if (isLine) mesh = new THREE.LineSegments(bufferGeometry, matOrMats);
    else if (isPoints) mesh = new THREE.Points(bufferGeometry, matOrMats as THREE.PointsMaterial);
    else mesh = new THREE.Mesh(bufferGeometry, matOrMats);
    mesh.name = obj.name;
    container.add(mesh);
  }
  return container;
}

interface StepMesh {
  positions: Float32Array;
  normals: Float32Array | null;
  indices: Uint32Array | null;
  color: number;
  name: string;
}

function rebuildGroupFromStepMeshes(meshes: StepMesh[]): THREE.Group {
  const group = new THREE.Group();
  const overallBox = new THREE.Box3();
  for (const [index, meshData] of meshes.entries()) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(meshData.positions, 3));
    if (meshData.normals) geometry.setAttribute('normal', new THREE.Float32BufferAttribute(meshData.normals, 3));
    else geometry.computeVertexNormals();
    if (meshData.indices) geometry.setIndex(new THREE.BufferAttribute(meshData.indices, 1));
    const material = createModelMaterial();
    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = meshData.name || `mesh_${index}`;
    group.add(mesh);
    geometry.computeBoundingBox();
    if (geometry.boundingBox) overallBox.union(geometry.boundingBox);
  }
  if (!overallBox.isEmpty()) {
    const center = overallBox.getCenter(new THREE.Vector3());
    const size = overallBox.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = 100 / maxDim;
    for (const child of group.children) {
      if (child instanceof THREE.Mesh) {
        child.scale.setScalar(scale);
        child.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
        child.position.y += (size.y * scale) / 2;
      }
    }
  }
  return group;
}

function createModelGroup(geometry: THREE.BufferGeometry): THREE.Group {
  const material = createModelMaterial();
  const mesh = new THREE.Mesh(geometry, material);
  geometry.computeBoundingBox();
  const box = geometry.boundingBox;
  if (!box) throw new Error('Failed to compute bounding box');
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  geometry.translate(-center.x, -center.y, -center.z);
  const maxDim = Math.max(size.x, size.y, size.z);
  const scale = 100 / maxDim;
  mesh.scale.setScalar(scale);
  mesh.position.y = (size.y * scale) / 2;
  const group = new THREE.Group();
  group.add(mesh);
  return group;
}

function convert3MFGroup(group3MF: THREE.Group): THREE.Group {
  const group = new THREE.Group();
  const box = new THREE.Box3();
  group3MF.traverse((child) => { if (child instanceof THREE.Mesh) { child.geometry.computeBoundingBox(); if (child.geometry.boundingBox) box.expandByObject(child); } });
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const scale = 100 / maxDim;
  group3MF.scale.setScalar(scale);
  group3MF.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
  group3MF.position.y += (size.y * scale) / 2;
  group.add(group3MF);
  return group;
}

function convertOBJGroup(groupOBJ: THREE.Group): THREE.Group {
  const group = new THREE.Group();
  const box = new THREE.Box3();
  groupOBJ.traverse((child) => { if (child instanceof THREE.Mesh) { child.geometry.computeBoundingBox(); if (child.geometry.boundingBox) box.expandByObject(child); } });
  const center = box.getCenter(new THREE.Vector3());
  const size = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const scale = 100 / maxDim;
  groupOBJ.scale.setScalar(scale);
  groupOBJ.position.set(-center.x * scale, -center.y * scale, -center.z * scale);
  groupOBJ.position.y += (size.y * scale) / 2;
  groupOBJ.traverse((child) => { if (child instanceof THREE.Mesh && (!child.material || (child.material as THREE.Material).name === '')) child.material = createModelMaterial(); });
  group.add(groupOBJ);
  return group;
}

// ============================================================================
// Scene setup (from streamline sceneSetup.ts)
// ============================================================================

let _cachedEnvMap: THREE.Texture | null = null;

function createRenderer(width: number, height: number): THREE.WebGLRenderer {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setSize(width, height);
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  return renderer;
}

function createCamera(aspect: number): THREE.PerspectiveCamera {
  const camera = new THREE.PerspectiveCamera(45, aspect, 0.1, 1000);
  camera.position.set(150, 100, 150);
  camera.lookAt(0, 0, 0);
  return camera;
}

function createLighting(scene: THREE.Scene): void {
  scene.add(new THREE.HemisphereLight(0xb1c6e0, 0x8c7e6a, 0.5));
  const keyLight = new THREE.DirectionalLight(0xfff5e6, 1.0);
  keyLight.position.set(5, 8, 4);
  scene.add(keyLight);
  const fillLight = new THREE.DirectionalLight(0xc4d4ef, 0.4);
  fillLight.position.set(-4, 4, -3);
  scene.add(fillLight);
  const rimLight = new THREE.DirectionalLight(0xffffff, 0.3);
  rimLight.position.set(-2, 6, -6);
  scene.add(rimLight);
}

function applyEnvironment(scene: THREE.Scene, renderer: THREE.WebGLRenderer): void {
  if (!_cachedEnvMap) {
    const pmremGenerator = new THREE.PMREMGenerator(renderer);
    pmremGenerator.compileEquirectangularShader();
    const roomEnv = RoomEnvironment();
    _cachedEnvMap = pmremGenerator.fromScene(roomEnv, 0.04).texture;
    pmremGenerator.dispose();
  }
  scene.environment = _cachedEnvMap;
}

// ============================================================================
// Post-processing (from streamline postProcessing.ts)
// ============================================================================

interface PostProcessingResult {
  composer: EffectComposer;
  ssaoPass: SSAOPass | null;
  fxaaPass: ShaderPass;
  resize: (width: number, height: number) => void;
  dispose: () => void;
}

function createPostProcessing(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.PerspectiveCamera,
): PostProcessingResult {
  const size = renderer.getSize(new THREE.Vector2());
  const pixelRatio = renderer.getPixelRatio();
  const composer = new EffectComposer(renderer);
  composer.addPass(new RenderPass(scene, camera));
  const ssaoPass = new SSAOPass(scene, camera, size.x * pixelRatio, size.y * pixelRatio);
  ssaoPass.kernelRadius = 0.5;
  ssaoPass.minDistance = 0.0005;
  ssaoPass.maxDistance = 0.05;
  ssaoPass.output = 0 as SSAOPass['output'];
  composer.addPass(ssaoPass);
  const fxaaPass = new ShaderPass(FXAAShader);
  fxaaPass.material.uniforms.resolution.value.set(1 / (size.x * pixelRatio), 1 / (size.y * pixelRatio));
  composer.addPass(fxaaPass);
  composer.addPass(new OutputPass());

  return {
    composer,
    ssaoPass,
    fxaaPass,
    resize: (width: number, height: number) => {
      const pr = renderer.getPixelRatio();
      composer.setSize(width, height);
      ssaoPass.setSize(width * pr, height * pr);
      fxaaPass.material.uniforms.resolution.value.set(1 / (width * pr), 1 / (height * pr));
    },
    dispose: () => composer.dispose(),
  };
}

// ============================================================================
// Grid (from streamline groundShadow.ts)
// ============================================================================

function createSubtleGrid(rendererSize: THREE.Vector2): THREE.Group {
  const group = new THREE.Group();
  group.name = '__grid';
  const GRID_SIZE = 400, DIVISIONS = 20, STEP = GRID_SIZE / DIVISIONS, HALF = GRID_SIZE / 2;
  const LINE_COLOR = new THREE.Color(0x555555);
  const CENTER_COLOR = new THREE.Color(0x000000);
  const lineMaterialBase = new LineMaterial({ color: LINE_COLOR, linewidth: 1, transparent: true, opacity: 0.5, depthWrite: false, resolution: rendererSize });
  const lineMaterialCenter = new LineMaterial({ color: CENTER_COLOR, linewidth: 2, transparent: true, opacity: 0.7, depthWrite: false, resolution: rendererSize });
  const Y = 0.0;
  function makeLine(x1: number, z1: number, x2: number, z2: number, mat: LineMaterial): Line2 {
    const geo = new LineGeometry();
    geo.setPositions([x1, Y, z1, x2, Y, z2]);
    return new Line2(geo, mat);
  }
  for (let i = 0; i <= DIVISIONS; i++) {
    const x = -HALF + i * STEP;
    group.add(makeLine(x, -HALF, x, HALF, i === DIVISIONS / 2 ? lineMaterialCenter : lineMaterialBase));
  }
  for (let i = 0; i <= DIVISIONS; i++) {
    const z = -HALF + i * STEP;
    group.add(makeLine(-HALF, z, HALF, z, i === DIVISIONS / 2 ? lineMaterialCenter : lineMaterialBase));
  }
  return group;
}

function updateGridResolution(grid: THREE.Group, size: THREE.Vector2): void {
  grid.traverse((obj) => {
    if ((obj as any).isLine2) ((obj as any).material as LineMaterial).resolution.set(size.x, size.y);
  });
}

// ============================================================================
// View controls UI (rendered as plain HTML, not React)
// ============================================================================

function createViewControlsHTML(
  container: HTMLElement,
  onViewModeChange: (mode: ViewMode) => void,
  onResetCamera: () => void,
): { updateActive: (mode: ViewMode) => void; element: HTMLElement } {
  const wrapper = document.createElement('div');
  wrapper.style.cssText = 'position:absolute;bottom:20px;left:50%;transform:translateX(-50%);display:flex;align-items:center;gap:8px;z-index:10;';

  // Reset camera button
  const resetBtn = document.createElement('button');
  resetBtn.title = 'Reset Camera';
  resetBtn.style.cssText = 'background:#fff;width:48px;height:48px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 4px 12px rgba(0,0,0,0.15);border:none;cursor:pointer;transition:background 0.2s;';
  resetBtn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="#666"><path d="M8 17q-.825 0-1.412-.587T6 15V9q0-.825.588-1.412T8 7h1l.7-.7q.15-.15.338-.225T10.425 6h3.15q.2 0 .388.075t.337.225l.7.7h1q.825 0 1.412.588T18 9v6q0 .825-.587 1.413T16 17zm4-3q.825 0 1.413-.587T14 12t-.587-1.412T12 10t-1.412.588T10 12t.588 1.413T12 14m0 10q-4.4 0-7.712-2.762T.174 14.275Q.1 13.85.337 13.5T1 13.1t.75.213t.4.687q.65 3.275 3.113 5.475T11.1 21.95l-.85-.85q-.275-.275-.275-.7t.275-.7t.7-.275t.7.275l3.3 3.3q.175.175.113.4t-.288.275q-.675.175-1.362.25T12 24m0-24q4.4 0 7.713 2.75t4.112 6.975q.075.425-.162.775t-.663.4t-.75-.213t-.4-.687q-.65-3.275-3.113-5.475T12.9 2.05l.85.85q.275.275.275.7t-.275.7t-.7.275t-.7-.275L9.05 1Q8.875.825 8.938.6t.287-.275Q9.9.15 10.588.075T12 0"/></svg>';
  resetBtn.addEventListener('click', onResetCamera);
  resetBtn.addEventListener('mouseenter', () => { resetBtn.style.background = '#f5f5f5'; });
  resetBtn.addEventListener('mouseleave', () => { resetBtn.style.background = '#fff'; });
  wrapper.appendChild(resetBtn);

  // View mode pill
  const pill = document.createElement('div');
  pill.style.cssText = 'background:#fff;padding:10px 20px;border-radius:9999px;display:flex;gap:16px;box-shadow:0 4px 12px rgba(0,0,0,0.15);';

  const modes: Array<{ mode: ViewMode; title: string; svg: string }> = [
    { mode: 'solid', title: 'Solid View', svg: '<path d="M477-80q-83 0-156-31.5T194-197q-54-54-85.5-127T77-480q0-83 31.5-156T194-763q54-54 127-85.5T477-880q83 0 156 31.5T760-763q54 54 85.5 127T877-480q0 83-31.5 156T760-197q-54 54-127 85.5T477-80Z"/>' },
    { mode: 'wireframe', title: 'Wireframe View', svg: '<path d="M280-120v-80h80v80h-80Zm160 0v-80h80v80h-80Zm160 0v-80h80v80h-80Zm160 0v-80h80v80h-80Zm0-160v-80h80v80h-80Zm0-160v-80h80v80h-80Zm0-160v-80h80v80h-80ZM120-120v-720h720v80H200v640h-80Z"/>' },
    { mode: 'xray', title: 'X-Ray View', svg: '<path d="M160-80q-33 0-56.5-23.5T80-160v-480q0-33 23.5-56.5T160-720h80v-80q0-33 23.5-56.5T320-880h480q33 0 56.5 23.5T880-800v480q0 33-23.5 56.5T800-240h-80v80q0 33-23.5 56.5T640-80H160Zm160-240h480v-480H320v480Z"/>' },
  ];

  const buttons: HTMLButtonElement[] = [];
  for (const { mode, title, svg } of modes) {
    const btn = document.createElement('button');
    btn.title = title;
    btn.dataset.mode = mode;
    btn.style.cssText = 'width:36px;height:36px;border:none;background:none;cursor:pointer;display:flex;align-items:center;justify-content:center;border-radius:50%;transition:all 0.2s;';
    btn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20" fill="currentColor">${svg}</svg>`;
    btn.addEventListener('click', () => onViewModeChange(mode));
    pill.appendChild(btn);
    buttons.push(btn);
  }
  wrapper.appendChild(pill);
  container.appendChild(wrapper);

  function updateActive(mode: ViewMode) {
    for (const btn of buttons) {
      if (btn.dataset.mode === mode) {
        btn.style.background = '#3b82f6';
        btn.style.color = '#fff';
      } else {
        btn.style.background = 'none';
        btn.style.color = '#000';
      }
    }
  }
  updateActive('solid');

  return { updateActive, element: wrapper };
}

// ============================================================================
// Main viewer creation
// ============================================================================

function createViewer(container: HTMLElement, url: string, fileName: string): ViewerInstance {
  // State
  let currentViewMode: ViewMode = 'solid';
  let destroyed = false;
  const originalMaterials = new Map<THREE.Mesh, THREE.Material>();
  let modelPivot: THREE.Group | null = null;
  let modelCenter = new THREE.Vector3(0, 0, 0);
  let grid: THREE.Group | null = null;
  let animationFrame: number | undefined;

  // Container styling
  container.style.position = 'relative';
  container.style.background = '#fff';
  container.style.overflow = 'hidden';
  container.style.width = '100%';
  container.style.height = '100%';

  // Scene setup — start with a fallback size; ResizeObserver will correct it
  // once the container is laid out by Flutter's HtmlElementView.
  const width = container.clientWidth || 800;
  const height = container.clientHeight || 600;
  const renderer = createRenderer(width, height);
  renderer.domElement.style.display = 'block';
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  const scene = new THREE.Scene();
  const camera = createCamera(width / height);
  createLighting(scene);
  applyEnvironment(scene, renderer);
  container.appendChild(renderer.domElement);

  // Post-processing
  const postProcessing = createPostProcessing(renderer, scene, camera);

  // Loading indicator
  const loadingEl = document.createElement('div');
  loadingEl.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:rgba(255,255,255,0.9);padding:16px 20px;border-radius:8px;font-size:14px;color:#666;font-family:system-ui,sans-serif;z-index:20;';
  loadingEl.textContent = 'Loading 3D model...';
  container.appendChild(loadingEl);

  // Error indicator (hidden initially)
  const errorEl = document.createElement('div');
  errorEl.style.cssText = 'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fef2f2;border:1px solid #fecaca;padding:16px 20px;border-radius:8px;font-size:14px;color:#dc2626;font-family:system-ui,sans-serif;z-index:20;display:none;';
  errorEl.textContent = 'Failed to load 3D model';
  container.appendChild(errorEl);

  // View controls
  const controls = createViewControlsHTML(
    container,
    (mode: ViewMode) => {
      currentViewMode = mode;
      controls.updateActive(mode);
      applyViewMode(mode);
    },
    () => resetCamera(),
  );

  // Camera target for panning
  const cameraTarget = new THREE.Vector3(0, 0, 0);

  // Mouse controls
  let isDragging = false;
  let isPanning = false;
  let prevX = 0;
  let prevY = 0;

  const onContextMenu = (e: MouseEvent) => e.preventDefault();
  const onMouseDown = (e: MouseEvent) => {
    if (e.button === 1 || e.button === 2) { isPanning = true; e.preventDefault(); }
    else if (e.button === 0) isDragging = true;
    else return;
    prevX = e.clientX;
    prevY = e.clientY;
    renderer.domElement.style.cursor = isPanning ? 'move' : 'grabbing';
  };
  const onMouseMove = (e: MouseEvent) => {
    if (!isDragging && !isPanning) return;
    const dx = e.clientX - prevX;
    const dy = e.clientY - prevY;
    if (isPanning) {
      const panSpeed = 0.5;
      const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
      const up = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 1);
      const offset = new THREE.Vector3().addScaledVector(right, -dx * panSpeed).addScaledVector(up, dy * panSpeed);
      camera.position.add(offset);
      cameraTarget.add(offset);
      camera.lookAt(cameraTarget);
    } else if (modelPivot) {
      modelPivot.rotation.y += dx * 0.01;
      modelPivot.rotation.x += dy * 0.01;
    }
    prevX = e.clientX;
    prevY = e.clientY;
  };
  const onMouseUp = () => {
    isDragging = false;
    isPanning = false;
    renderer.domElement.style.cursor = 'grab';
  };
  const onAuxClick = (e: MouseEvent) => { if (e.button === 1) e.preventDefault(); };
  const onWheel = (e: WheelEvent) => {
    e.preventDefault();
    const zoomSpeed = 0.1;
    const scaleFactor = e.deltaY > 0 ? 1 + zoomSpeed : 1 - zoomSpeed;
    const offset = camera.position.clone().sub(cameraTarget);
    const newDist = offset.length() * scaleFactor;
    if (newDist > 50 && newDist < 500) {
      offset.multiplyScalar(scaleFactor);
      camera.position.copy(cameraTarget).add(offset);
    }
  };
  const doResize = () => {
    const w = container.clientWidth;
    const h = container.clientHeight;
    if (w === 0 || h === 0) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    postProcessing.resize(w, h);
    if (grid) updateGridResolution(grid, renderer.getSize(new THREE.Vector2()));
  };

  // Use ResizeObserver to detect when the container gets its actual size
  // from Flutter's HtmlElementView layout — window resize alone won't catch this.
  const resizeObserver = new ResizeObserver(() => doResize());
  resizeObserver.observe(container);

  renderer.domElement.style.cursor = 'grab';
  renderer.domElement.addEventListener('contextmenu', onContextMenu);
  renderer.domElement.addEventListener('mousedown', onMouseDown);
  renderer.domElement.addEventListener('auxclick', onAuxClick);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup', onMouseUp);
  renderer.domElement.addEventListener('wheel', onWheel, { passive: false });

  // Animation loop
  const animate = () => {
    if (destroyed) return;
    animationFrame = requestAnimationFrame(animate);
    postProcessing.composer.render();
  };
  animate();

  // Load model
  loadModelFromURL(url, fileName)
    .then((modelGroup) => {
      if (destroyed) return;

      // Store original materials
      modelGroup.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material) {
          originalMaterials.set(child, child.material as THREE.Material);
        }
      });

      // Compute bounding box
      scene.add(modelGroup);
      const box = new THREE.Box3().setFromObject(modelGroup);
      modelCenter = box.getCenter(new THREE.Vector3());
      scene.remove(modelGroup);

      // Create pivot for rotation around model center
      const pivot = new THREE.Group();
      pivot.position.copy(modelCenter);
      modelGroup.position.sub(modelCenter);
      pivot.add(modelGroup);

      // Add grid
      const groundY = box.min.y - modelCenter.y;
      const rendererSize = renderer.getSize(new THREE.Vector2());
      grid = createSubtleGrid(rendererSize);
      grid.position.y = groundY;
      pivot.add(grid);

      modelPivot = pivot;
      scene.add(pivot);

      // Position camera relative to model center
      cameraTarget.copy(modelCenter);
      camera.position.copy(modelCenter).add(new THREE.Vector3(150, 100, 150));
      camera.lookAt(cameraTarget);

      // Remove loading indicator
      loadingEl.style.display = 'none';
    })
    .catch((err) => {
      console.error('[ModelViewer] Load failed:', err);
      loadingEl.style.display = 'none';
      errorEl.style.display = 'block';
    });

  // View mode switching
  function applyViewMode(mode: ViewMode) {
    if (originalMaterials.size === 0) return;

    const isSolid = mode === 'solid';
    if (postProcessing.ssaoPass) postProcessing.ssaoPass.enabled = isSolid;

    for (const [mesh, original] of originalMaterials) {
      switch (mode) {
        case 'wireframe':
          mesh.material = new THREE.MeshBasicMaterial({ color: 0x555555, wireframe: true });
          break;
        case 'xray':
          mesh.material = new THREE.MeshStandardMaterial({
            color: 0x8a9099, transparent: true, opacity: 0.25,
            side: THREE.DoubleSide, depthWrite: false, roughness: 0.8, metalness: 0.0,
          });
          break;
        default:
          mesh.material = original;
          break;
      }
    }
  }

  function resetCamera() {
    if (!modelPivot) return;
    cameraTarget.copy(modelCenter);
    camera.position.copy(modelCenter).add(new THREE.Vector3(150, 100, 150));
    camera.lookAt(cameraTarget);
    modelPivot.rotation.set(0, 0, 0);
  }

  // Public API
  const instance: ViewerInstance = {
    get viewMode() { return currentViewMode; },
    setViewMode(mode: ViewMode) {
      currentViewMode = mode;
      controls.updateActive(mode);
      applyViewMode(mode);
    },
    resetCamera,
    destroy() {
      if (destroyed) {
        return;
      }
      destroyed = true;
      // Stop animation loop immediately
      if (animationFrame) cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      // Remove event listeners
      renderer.domElement.removeEventListener('contextmenu', onContextMenu);
      renderer.domElement.removeEventListener('mousedown', onMouseDown);
      renderer.domElement.removeEventListener('auxclick', onAuxClick);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      renderer.domElement.removeEventListener('wheel', onWheel);
      // Dispose Three.js resources (order matters)
      // 1. Dispose scene objects to release GPU buffers
      let meshCount = 0;
      scene.traverse((obj) => {
        if (obj instanceof THREE.Mesh) {
          meshCount++;
          obj.geometry?.dispose();
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m: THREE.Material) => m.dispose());
          } else if (obj.material) {
            (obj.material as THREE.Material).dispose();
          }
        }
      });
      scene.clear();
      originalMaterials.clear();

      // 2. Dispose post-processing render targets
      postProcessing.dispose();

      // 3. Force-release the WebGL context before disposing the renderer.
      const gl = renderer.getContext();
      const glContextValid = !gl.isContextLost();

      renderer.dispose();

      const loseCtx = gl.getExtension('WEBGL_lose_context');
      if (loseCtx) {
        loseCtx.loseContext();
      }

      // 4. Resize canvas to 1x1 to free framebuffer memory immediately
      renderer.domElement.width = 1;
      renderer.domElement.height = 1;

      // 5. Remove DOM elements
      if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      if (container.contains(controls.element)) container.removeChild(controls.element);
      if (container.contains(loadingEl)) container.removeChild(loadingEl);
      if (container.contains(errorEl)) container.removeChild(errorEl);
      // Clear references stored on the container
      delete (container as any).__viewerInstance;
    },
  };

  // Store instance on the container so it can be destroyed by reference
  // to the DOM element even if the Dart-side reference is lost.
  (container as any).__viewerInstance = instance;

  // Auto-destroy when the container is removed from the DOM.
  // Flutter's HtmlElementView may detach the element without Dart's
  // dispose() reliably calling destroy(), so this is a safety net.
  const disconnectObserver = new MutationObserver(() => {
    if (!document.body.contains(container) && !destroyed) {
      instance.destroy();
      disconnectObserver.disconnect();
    }
  });
  disconnectObserver.observe(document.body, { childList: true, subtree: true });

  return instance;
}

// ============================================================================
// Expose global API
// ============================================================================

interface ModelViewerFullAPI extends ModelViewerAPI {
  /** Destroy a viewer by its container element */
  destroyByContainer(container: HTMLElement): void;
}

const api: ModelViewerFullAPI = {
  create: createViewer,
  destroyByContainer(container: HTMLElement) {
    const instance = (container as any).__viewerInstance as ViewerInstance | undefined;
    if (instance) {
      instance.destroy();
      delete (container as any).__viewerInstance;
    }
  },
};

(window as any).ModelViewer = api;

export type { ViewerInstance, ModelViewerAPI, ViewMode };
